import { Component, effect, inject, input, output } from '@angular/core';
import { ApiProduct, ViewService } from 'api';
import { TranslateModule } from '@ngx-translate/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { CardHeaderComponent } from 'common/card/header/card-header.component';
import {
  dirtyErrorStateMatcher,
  FormService,
  requiredAndNotWhitespaceOnly,
} from 'common/form/form.service';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';
import { IconDirective } from 'common/icon/icon.directive';

export interface ProductEditModel {
  name: string;
  description: string;
}

@Component({
  selector: 'comp-product-header',
  templateUrl: './product-header.component.html',
  styleUrl: './product-header.component.scss',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatFormField,
    MatFormFieldModule,
    MatInput,
    MatLabel,
    CdkTextareaAutosize,
    CardHeaderComponent,
    AutofocusDirective,
    ButtonDirective,
    IconDirective,
  ],
})
export class ProductHeaderComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly viewService = inject(ViewService);
  protected readonly formService = inject(FormService);

  readonly details = input<ApiProduct | null>(null);
  readonly isEditing = input(false);
  readonly isSubmitting = input(false);
  readonly model = input.required<ProductEditModel>();

  readonly editRequested = output<void>();
  readonly cancelRequested = output<void>();
  readonly modelChanged = output<ProductEditModel>();
  readonly saveRequested = output<void>();

  protected readonly dirtyErrorStateMatcher = dirtyErrorStateMatcher;

  protected form: FormGroup = this.formBuilder.group({
    name: ['', [requiredAndNotWhitespaceOnly, Validators.maxLength(255)]],
    description: [''],
  });

  constructor() {
    effect(() => {
      const model = this.model();

      this.form.patchValue(
        {
          name: model.name,
          description: model.description,
        },
        { emitEvent: false },
      );
    });
  }

  protected onModelChanged(): void {
    this.modelChanged.emit(this.form.value);
  }

  protected async validateUniqueness(): Promise<void> {
    const name = this.form.get('name')?.value ?? '';
    const originalName = this.details()?.name ?? '';

    if (name === originalName) {
      this.formService.clearError(this.form, 'name', 'uniqueness');
      return;
    }

    await this.formService.runAsyncValidator(this.form, 'name', ['uniqueness'], () =>
      this.viewService.validateProductName(name),
    );
  }

  protected async onSave(): Promise<void> {
    await this.validateUniqueness();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.modelChanged.emit(this.form.value);
    this.saveRequested.emit();
  }
}
